// Minimal CSV parser + writer — handles quoted fields, escaped quotes ("") and
// CRLF. Good enough for roster uploads and report downloads without a dependency.

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

export function parseCSV(text: string): ParsedCsv {
  // Strip a UTF-8 BOM if present.
  const input = text.replace(/^﻿/, '');
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      record.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && input[i + 1] === '\n') i++;
      record.push(field);
      field = '';
      if (record.length > 1 || record[0] !== '') records.push(record);
      record = [];
    } else {
      field += ch;
    }
  }
  if (field !== '' || record.length > 0) {
    record.push(field);
    if (record.length > 1 || record[0] !== '') records.push(record);
  }

  if (records.length === 0) return { headers: [], rows: [] };

  const headers = records[0].map((h) => h.trim());
  const rows: Record<string, string>[] = [];
  for (let r = 1; r < records.length; r++) {
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = (records[r][idx] ?? '').trim();
    });
    rows.push(row);
  }
  return { headers, rows };
}

// ---- Writing ----------------------------------------------------------------

/** RFC-4180 field escaping: quote when the value contains a comma, quote or newline. */
function escapeField(value: string | number | null | undefined): string {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Serialize rows to CSV text. Pass an empty `headers` array to skip the header
 * line (useful for prepending metadata blocks to another CSV).
 */
export function toCSV(headers: string[], rows: (string | number | null | undefined)[][]): string {
  const lines: string[] = [];
  if (headers.length > 0) lines.push(headers.map(escapeField).join(','));
  for (const row of rows) lines.push(row.map(escapeField).join(','));
  return lines.join('\r\n');
}

/** Trigger a browser download of `csv` as `filename` (Blob + anchor). */
export function downloadCsv(filename: string, csv: string): void {
  // BOM so Excel opens UTF-8 content correctly.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
